import React, { useState, useRef, useEffect } from "react";
import {
  FileText,
  Image as ImageIcon,
  Headphones,
  User,
  MapPin,
  Radio,
  ListOrdered,
  FileSpreadsheet,
  Sparkles,
  MessageSquare,
  Camera,
  Check,
  ChevronDown
} from "lucide-react";
import { whatsappApi } from "../services/api";
import { normalizeWhatsAppPhone } from "../utils/helpers";

/**
 * WhatsAppComposer — v3 (with WhatsApp Web style attachments)
 * ─────────────────────────────────────────────────────────────────
 * <WhatsAppComposer defaultTo={lead.contactNumber} />
 * ─────────────────────────────────────────────────────────────────
 */

const MESSAGE_TYPES = [
  { value: "document", label: "Document", icon: FileText, color: "#7F66FF" },
  { value: "image", label: "Photos & videos", icon: ImageIcon, color: "#007BFC" },
  { value: "camera", label: "Camera", icon: Camera, color: "#FF2E74" },
  { value: "audio", label: "Audio", icon: Headphones, color: "#FF6B00" },
  { value: "contact", label: "Contact", icon: User, color: "#00B2FF" },
  { value: "location", label: "Location", icon: MapPin, color: "#10B981" },
  { value: "buttons", label: "Buttons", icon: Radio, color: "#64748B" },
  { value: "list", label: "List", icon: ListOrdered, color: "#F59E0B" },
  { value: "template", label: "Template", icon: FileSpreadsheet, color: "#8B5CF6" },
  { value: "sticker", label: "New sticker", icon: Sparkles, color: "#00A884" },
  { value: "text", label: "Text", icon: MessageSquare, color: "#25D366" },
];

const MEDIA_TYPES = ["image", "camera", "video", "audio", "document", "sticker"];

const ACCEPT_MAP = {
  image: "image/jpeg,image/png,image/webp",
  camera: "image/jpeg,image/png,image/webp",
  video: "video/mp4,video/3gpp",
  audio: "audio/aac,audio/mpeg,audio/amr,audio/ogg,audio/mp4",
  document: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt",
  sticker: "image/webp",
};

const initialFormState = {
  to: "",
  message: "",
  mediaUrl: "",
  filename: "",
  templateName: "hello_world",
  latitude: "",
  longitude: "",
  locationName: "",
  locationAddress: "",
  buttonLabelsRaw: "",
  listButtonText: "",
  listOptionsRaw: "",
  contactName: "",
  contactPhone: "",
};

export default function WhatsAppComposer({
  defaultTo = "",
  initialMessageType = "text",
  hideTypeSelector = false,
  onSent,
}) {
  const [messageType, setMessageType] = useState(initialMessageType);
  const [form, setForm] = useState({ ...initialFormState, to: defaultTo });
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);

  useEffect(() => {
    setMessageType(initialMessageType);
    setResult(null);
    setUploadError(null);
  }, [initialMessageType]);

  const startCamera = async () => {
    setUploadError(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false
      });
      setCameraStream(mediaStream);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 50);
    } catch (err) {
      console.error("Camera access error:", err);
      setUploadError("Could not access camera: " + err.message);
      // Fallback to native file picker
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  };

  useEffect(() => {
    if (messageType === "camera" && !form.mediaUrl) {
      startCamera();
    } else {
      stopCamera();
    }

    if (MEDIA_TYPES.includes(messageType) && messageType !== "camera" && !form.mediaUrl) {
      const timer = setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [messageType, form.mediaUrl]);

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const resetFieldsForType = (nextType) => {
    setMessageType(nextType);
    setResult(null);
    setUploadError(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "camera_capture.jpg", { type: "image/jpeg" });
      
      stopCamera();
      setUploading(true);
      setUploadError(null);
      try {
        const data = await whatsappApi.uploadMedia(file, "image");
        const uploadedUrl = data?.url || data?.mediaUrl || data?.fileUrl || data?.data?.url || data?.data?.mediaUrl;
        if (data && (data.success || uploadedUrl) && uploadedUrl) {
          updateField("mediaUrl", uploadedUrl);
          updateField("filename", file.name);
        } else {
          setUploadError(data?.error || data?.message || "Upload failed.");
        }
      } catch (err) {
        setUploadError(err.message || "Upload failed.");
      } finally {
        setUploading(false);
      }
    }, "image/jpeg");
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    setResult(null);
    try {
      const uploadType = messageType === "camera" ? "image" : messageType;
      const data = await whatsappApi.uploadMedia(file, uploadType);
      const uploadedUrl = data?.url || data?.mediaUrl || data?.fileUrl || data?.data?.url || data?.data?.mediaUrl;
      if (data && (data.success || uploadedUrl) && uploadedUrl) {
        updateField("mediaUrl", uploadedUrl);
        updateField("filename", file.name);
      } else {
        setUploadError(data?.error || data?.message || "Upload failed.");
      }
    } catch (err) {
      setUploadError(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const validate = () => {
    if (!form.to.trim()) return "Recipient phone number is required.";
    if (MEDIA_TYPES.includes(messageType) && !form.mediaUrl.trim())
      return "Please upload a file or paste a media URL.";
    if (messageType === "text" && !form.message.trim()) return "Message text is required.";
    if (messageType === "location" && (!form.latitude || !form.longitude))
      return "Latitude and longitude are required.";
    if (messageType === "buttons") {
      const count = form.buttonLabelsRaw.split(",").map((s) => s.trim()).filter(Boolean).length;
      if (count < 1) return "At least one button label is required.";
      if (count > 3) return "Maximum 3 buttons allowed.";
    }
    if (messageType === "list") {
      const count = form.listOptionsRaw.split(",").map((s) => s.trim()).filter(Boolean).length;
      if (count < 1) return "At least one list option is required.";
      if (count > 10) return "Maximum 10 list options allowed.";
    }
    if (messageType === "contact" && (!form.contactName.trim() || !form.contactPhone.trim()))
      return "Contact name and phone are required.";
    if (messageType === "template" && !form.templateName.trim()) return "Template name is required.";
    return null;
  };

  const handleSend = async () => {
    const validationError = validate();
    if (validationError) {
      setResult({ success: false, error: validationError });
      return;
    }
    setSending(true);
    setResult(null);

    try {
      const to = normalizeWhatsAppPhone(form.to.trim());
      let data;
      switch (messageType) {
        case "text": data = await whatsappApi.sendText(to, form.message); break;
        case "image":
        case "camera": data = await whatsappApi.sendImage(to, form.mediaUrl, form.message); break;
        case "video": data = await whatsappApi.sendVideo(to, form.mediaUrl, form.message); break;
        case "audio": data = await whatsappApi.sendAudio(to, form.mediaUrl); break;
        case "document": data = await whatsappApi.sendDocument(to, form.mediaUrl, form.filename); break;
        case "sticker": data = await whatsappApi.sendSticker(to, form.mediaUrl); break;
        case "location":
          data = await whatsappApi.sendLocation(to, parseFloat(form.latitude), parseFloat(form.longitude), form.locationName, form.locationAddress);
          break;
        case "buttons": {
          const labels = form.buttonLabelsRaw.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3);
          data = await whatsappApi.sendButtons(to, form.message, labels);
          break;
        }
        case "list": {
          const options = form.listOptionsRaw.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 10);
          data = await whatsappApi.sendList(to, form.message, form.listButtonText, options);
          break;
        }
        case "contact": data = await whatsappApi.sendContact(to, form.contactName, form.contactPhone); break;
        case "template": data = await whatsappApi.sendTemplate(to, form.templateName || "hello_world", "en_US"); break;
        default: data = await whatsappApi.sendText(to, form.message);
      }

      if (data && (data.success || data.status === "success" || data.messageId)) {
        setResult({ success: true, messageId: data.messageId });
        onSent?.(data);
      } else {
        setResult({ success: false, error: data?.error || data?.message || "Send failed." });
      }
    } catch (err) {
      setResult({ success: false, error: err.message || "Something went wrong." });
    } finally {
      setSending(false);
    }
  };

  const showMediaPreview = ["image", "camera", "video"].includes(messageType) && form.mediaUrl.trim();

  return (
    <div style={s.container}>
      <style>{`
        .wa-menu-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 6px;
          margin-top: 6px;
          max-height: 250px;
          overflow-y: auto;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f1f5f9;
        }
        .wa-menu-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .wa-menu-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          border-radius: 10px;
          border: none;
          background: transparent;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          width: 100%;
        }
        .wa-menu-item:hover {
          background-color: #f4f4f5;
        }
        .wa-menu-item-active {
          background-color: #f4f4f5 !important;
        }
        .wa-item-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .wa-item-label {
          font-size: 14px;
          font-weight: 500;
          color: #27272a;
          font-family: inherit;
        }
        .wa-menu-item-active .wa-item-label {
          font-weight: 600;
          color: #09090b;
        }
        .wa-send-btn {
          margin-top: 12px;
          padding: 12px 24px;
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
          background-color: #00a884;
          border: none;
          border-radius: 24px;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 168, 132, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          width: 100%;
        }
        .wa-send-btn:hover {
          background-color: #008f72;
          box-shadow: 0 4px 12px rgba(0, 168, 132, 0.35);
        }
        .wa-send-btn:active {
          transform: scale(0.98);
        }
      `}</style>
      {/* ── Header (commented out) ──
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.headerIcon}>💬</span>
          <div>
            <div style={s.headerTitle}>Send WhatsApp Message</div>
            <div style={s.headerSubtitle}>via Cloud API</div>
          </div>
        </div>
      </div>
      */}

      <div style={s.body}>
        {/* ── Recipient Field (commented out) ──
        <Field label="Recipient">
          <input
            style={s.input}
            type="text"
            placeholder="919999999999 (with country code)"
            value={form.to}
            onChange={(e) => updateField("to", e.target.value)}
          />
        </Field>
        */}

        {!hideTypeSelector && (
          <div>
            <label style={s.label}>Message type / Attachments</label>
            <div className="wa-menu-card">
              <div className="wa-menu-list">
                {MESSAGE_TYPES.map((t) => {
                  const IconComponent = t.icon;
                  const isSelected = messageType === t.value;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => resetFieldsForType(t.value)}
                      className={`wa-menu-item ${isSelected ? "wa-menu-item-active" : ""}`}
                    >
                      <div className="wa-item-left">
                        <div style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: t.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0
                        }}>
                          <IconComponent size={16} color="#ffffff" strokeWidth={2.5} />
                        </div>
                        <span className="wa-item-label">
                          {t.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Text Message Box (commented out) ──
        {messageType === "text" && (
          <Field label="Message">
            <textarea
              style={s.textarea}
              rows={4}
              placeholder="Type your message..."
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
            />
          </Field>
        )}
        */}

        {MEDIA_TYPES.includes(messageType) && messageType !== "camera" && (
          <div style={s.field}>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_MAP[messageType]}
              onChange={handleFileSelect}
              style={{ display: "none" }}
              id="wa-file-input"
            />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "20px 10px" }}>
              {uploading ? (
                <span style={{ fontSize: 13, color: "#475569", display: "flex", alignItems: "center", gap: 8 }}>
                  <Spinner /> Uploading file...
                </span>
              ) : !form.mediaUrl ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="wa-send-btn"
                >
                  Select {messageType[0].toUpperCase() + messageType.slice(1)}
                </button>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                    📄 Selected: {form.filename || "File uploaded"}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      updateField("mediaUrl", "");
                      updateField("filename", "");
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#dc2626",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      textDecoration: "underline"
                    }}
                  >
                    Remove File
                  </button>
                </div>
              )}
              {uploadError && <div style={s.uploadErrorText}>⚠️ {uploadError}</div>}
            </div>

            {["image", "video"].includes(messageType) && form.mediaUrl && (
              <Field label="Caption (optional)">
                <input style={s.input} type="text" value={form.message} onChange={(e) => updateField("message", e.target.value)} />
              </Field>
            )}
          </div>
        )}

        {messageType === "camera" && (
          <div style={s.field}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              id="wa-file-input"
            />
            
            {!form.mediaUrl && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center", width: "100%" }}>
                {cameraStream ? (
                  <>
                    <div style={{ width: "100%", borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0", background: "#000", aspectRatio: "4/3", display: "flex", justifyContent: "center" }}>
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </div>
                    <div style={{ display: "flex", gap: 12, width: "100%" }}>
                      <button
                        type="button"
                        onClick={capturePhoto}
                        className="wa-send-btn"
                        style={{ flex: 1 }}
                      >
                        📸 Take Photo
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          stopCamera();
                          fileInputRef.current?.click();
                        }}
                        className="wa-send-btn"
                        style={{ flex: 1, backgroundColor: "#64748b" }}
                      >
                        📂 Choose File
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center", width: "100%", padding: "20px 10px" }}>
                    {uploading ? (
                      <span style={{ fontSize: 13, color: "#475569", display: "flex", alignItems: "center", gap: 8 }}>
                        <Spinner /> Uploading image...
                      </span>
                    ) : (
                      <>
                        <span style={{ fontSize: 13, color: "#475569" }}>Starting camera...</span>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="wa-send-btn"
                        >
                          📂 Choose File
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {form.mediaUrl && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                  📄 Selected: {form.filename || "Captured Image"}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    updateField("mediaUrl", "");
                    updateField("filename", "");
                    startCamera(); // restart camera
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#dc2626",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline"
                  }}
                >
                  Retake Photo
                </button>
                
                <Field label="Caption (optional)">
                  <input style={s.input} type="text" value={form.message} onChange={(e) => updateField("message", e.target.value)} />
                </Field>
              </div>
            )}
            
            {uploadError && <div style={{ ...s.uploadErrorText, textAlign: "center" }}>⚠️ {uploadError}</div>}
          </div>
        )}

        {messageType === "location" && (
          <>
            <Row>
              <Field label="Latitude">
                <input style={s.input} type="text" value={form.latitude} onChange={(e) => updateField("latitude", e.target.value)} />
              </Field>
              <Field label="Longitude">
                <input style={s.input} type="text" value={form.longitude} onChange={(e) => updateField("longitude", e.target.value)} />
              </Field>
            </Row>
            <Field label="Location name (optional)">
              <input style={s.input} type="text" value={form.locationName} onChange={(e) => updateField("locationName", e.target.value)} />
            </Field>
            <Field label="Address (optional)">
              <input style={s.input} type="text" value={form.locationAddress} onChange={(e) => updateField("locationAddress", e.target.value)} />
            </Field>
          </>
        )}

        {messageType === "buttons" && (
          <>
            <Field label="Body text">
              <input style={s.input} type="text" placeholder="Confirm your order?" value={form.message} onChange={(e) => updateField("message", e.target.value)} />
            </Field>
            <Field label="Button labels (comma-separated, max 3)">
              <input style={s.input} type="text" placeholder="Yes, No, Talk to agent" value={form.buttonLabelsRaw} onChange={(e) => updateField("buttonLabelsRaw", e.target.value)} />
            </Field>
          </>
        )}

        {messageType === "list" && (
          <>
            <Field label="Body text">
              <input style={s.input} type="text" placeholder="Please choose a delivery slot" value={form.message} onChange={(e) => updateField("message", e.target.value)} />
            </Field>
            <Field label="List button text">
              <input style={s.input} type="text" placeholder="Choose Slot" value={form.listButtonText} onChange={(e) => updateField("listButtonText", e.target.value)} />
            </Field>
            <Field label="Options (comma-separated, max 10)">
              <input style={s.input} type="text" placeholder="10 AM - 12 PM, 2 PM - 4 PM" value={form.listOptionsRaw} onChange={(e) => updateField("listOptionsRaw", e.target.value)} />
            </Field>
          </>
        )}

        {messageType === "contact" && (
          <Row>
            <Field label="Contact name">
              <input style={s.input} type="text" value={form.contactName} onChange={(e) => updateField("contactName", e.target.value)} />
            </Field>
            <Field label="Contact phone">
              <input style={s.input} type="text" value={form.contactPhone} onChange={(e) => updateField("contactPhone", e.target.value)} />
            </Field>
          </Row>
        )}

        {messageType === "template" && (
          <Field label="Template name (e.g. hello_world)">
            <input style={s.input} type="text" placeholder="hello_world" value={form.templateName} onChange={(e) => updateField("templateName", e.target.value)} />
          </Field>
        )}

        {(!MEDIA_TYPES.includes(messageType) || !!form.mediaUrl) && (
          <button className="wa-send-btn" style={{ opacity: sending || uploading ? 0.65 : 1 }} onClick={handleSend} disabled={sending || uploading}>
            {sending ? (
              <span style={s.sendingRow}><Spinner /> Sending…</span>
            ) : (
              "Send Message"
            )}
          </button>
        )}

        {result && (
          <div style={{ ...s.resultBox, ...(result.success ? s.resultSuccess : s.resultError) }}>
            {result.success ? (
              <span>✅ Sent successfully{result.messageId ? ` — ID: ${result.messageId.slice(0, 24)}…` : ""}</span>
            ) : (
              <span>❌ {result.error}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div style={s.row}>{children}</div>;
}

function Spinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" style={{ animation: "wa-spin 0.8s linear infinite" }}>
      <style>{`@keyframes wa-spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="12" cy="12" r="9" fill="none" stroke="#fff" strokeWidth="3" strokeDasharray="42" strokeLinecap="round" opacity="0.9" />
    </svg>
  );
}

const s = {
  container: {
    maxWidth: 460, borderRadius: 16, fontFamily: "Inter, system-ui, sans-serif",
    background: "#ffffff", boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08)",
    border: "1px solid #eef2f6", overflow: "hidden",
  },
  header: { padding: "18px 22px", background: "linear-gradient(135deg, #25D366 0%, #1FA855 100%)" },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  headerIcon: {
    width: 38, height: 38, borderRadius: 12, background: "rgba(255,255,255,0.22)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
  },
  headerTitle: { fontWeight: 700, fontSize: 16, color: "#fff", lineHeight: 1.2 },
  headerSubtitle: { fontSize: 12, color: "rgba(255,255,255,0.85)", marginTop: 2 },
  body: { padding: 22, display: "flex", flexDirection: "column", gap: 16 },
  field: { display: "flex", flexDirection: "column", gap: 6, flex: 1 },
  row: { display: "flex", gap: 12 },
  label: { fontSize: 12.5, fontWeight: 600, color: "#475569", letterSpacing: 0.2 },
  input: {
    padding: "10px 12px", fontSize: 14, borderRadius: 10, border: "1.5px solid #e2e8f0",
    outline: "none", background: "#fbfcfe",
  },
  textarea: {
    padding: "10px 12px", fontSize: 14, borderRadius: 10, border: "1.5px solid #e2e8f0",
    outline: "none", resize: "vertical", fontFamily: "inherit", background: "#fbfcfe",
  },
  uploadRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 2 },
  uploadButton: {
    padding: "9px 14px", fontSize: 13, fontWeight: 600, color: "#fff",
    background: "#1e293b", borderRadius: 9, cursor: "pointer", whiteSpace: "nowrap",
  },
  uploadOr: { fontSize: 12, color: "#94a3b8" },
  uploadErrorText: { fontSize: 12, color: "#dc2626", marginTop: 2 },
  waMenuCard: {
    background: "#ffffff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
    padding: "6px",
    marginTop: 6,
    maxHeight: 240,
    overflowY: "auto",
  },
  waMenuList: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  waMenuItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderRadius: 10,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    transition: "all 0.15s ease",
    textAlign: "left",
    width: "100%",
  },
  waMenuItemActive: {
    background: "#f0fdf4",
  },
  waItemLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  waItemLabel: {
    fontSize: 13.5,
    fontFamily: "inherit",
  },
  selectedBadge: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#25D366",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  previewBox: {
    borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0", background: "#f8fafc",
    display: "flex", justifyContent: "center", maxHeight: 220,
  },
  previewImg: { maxWidth: "100%", maxHeight: 220, objectFit: "contain" },
  sendButton: {
    marginTop: 4, padding: "13px 16px", fontSize: 14.5, fontWeight: 700, color: "#fff",
    background: "linear-gradient(135deg, #25D366 0%, #1FA855 100%)", border: "none",
    borderRadius: 12, cursor: "pointer", boxShadow: "0 4px 14px rgba(37,211,102,0.35)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  sendingRow: { display: "flex", alignItems: "center", gap: 8 },
  resultBox: { padding: "11px 14px", borderRadius: 10, fontSize: 13.5, lineHeight: 1.5 },
  resultSuccess: { background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" },
  resultError: { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
};